import { useEffect, useRef, useState } from 'react';

import { fetchPlansStatus, type PlansStatus } from '../Plans/plansGenerated';
import { fetchDataStatus, type PeDataStatus } from '../ProgramEvaluation/dataStatus';

export type SampleGateSources = {
  dataStatus: () => Promise<PeDataStatus>;
  plansStatus: () => Promise<PlansStatus>;
};

export const serverSampleGateSources: SampleGateSources = {
  dataStatus: fetchDataStatus,
  plansStatus: fetchPlansStatus,
};

export type SampleGateRole = { isSuperAdmin: boolean; isDistrictAdmin: boolean };

export type SampleGateState = 'pending' | 'absent' | 'present' | 'unknown';

export type SampleGate = {
  plan: SampleGateState;
  programEvaluation: SampleGateState;
  sisConnected: boolean;
  accessUploaded: boolean;
  sisMatchingConfirmed: boolean;
  sisMatchingRequired: boolean;
  schoolYear: string | null;
};

const PENDING: SampleGate = {
  plan: 'pending',
  programEvaluation: 'pending',
  sisConnected: false,
  accessUploaded: false,
  sisMatchingConfirmed: false,
  sisMatchingRequired: false,
  schoolYear: null,
};
const OPEN: SampleGate = { ...PENDING, plan: 'absent', programEvaluation: 'absent' };
const UNKNOWN: SampleGate = { ...PENDING, plan: 'unknown', programEvaluation: 'unknown' };

function presence(real: boolean): SampleGateState {
  return real ? 'present' : 'absent';
}

function adminGate(status: PeDataStatus): SampleGate {
  const sisMatchingConfirmed = status.sisMatchingConfirmedAt !== null;
  const sisMatchingRequired = status.sisMatchingRequired;
  return {
    plan: presence(status.accessPresent),
    programEvaluation: presence(status.accessPresent && status.rosterStreamConnected && (!sisMatchingRequired || sisMatchingConfirmed)),
    sisConnected: status.rosterStreamConnected,
    accessUploaded: status.accessPresent,
    sisMatchingConfirmed,
    sisMatchingRequired,
    schoolYear: status.schoolYear,
  };
}

export function useSampleGate(role: SampleGateRole, sources: SampleGateSources): SampleGate {
  const [gate, setGate] = useState<SampleGate>(PENDING);
  const sourcesRef = useRef(sources);
  const { isSuperAdmin, isDistrictAdmin } = role;

  useEffect(() => {
    if (isSuperAdmin) return undefined;
    let active = true;
    if (isDistrictAdmin) {
      sourcesRef.current
        .dataStatus()
        .then((status) => {
          if (active) setGate(adminGate(status));
        })
        .catch((err) => {
          console.warn('sample previews: ACCESS data status unavailable, keeping the samples closed', err);
          if (active) setGate(UNKNOWN);
        });
    } else {
      sourcesRef.current
        .plansStatus()
        .then((plans) => {
          if (active) setGate({ ...UNKNOWN, plan: presence(plans.generated) });
        })
        .catch((err) => {
          console.warn('sample previews: plans status unavailable, keeping the plan sample closed', err);
          if (active) setGate(UNKNOWN);
        });
    }
    return () => {
      active = false;
    };
  }, [isSuperAdmin, isDistrictAdmin]);

  if (isSuperAdmin) return OPEN;
  return gate;
}
